import React, { useEffect, useRef } from "react"
import { Box } from "@chakra-ui/react"

type Props = {
  srcs: string[]
  /** Called once when all images have loaded. */
  onReady?: () => void
}

/**
 * Renders hidden 1px imgs to keep decoded bitmaps alive in the browser's image
 * cache. Without DOM presence the browser evicts decoded bitmaps under memory
 * pressure, causing visible re-decode lag on remount even when bytes are cached.
 *
 * Pass `onReady` to be notified when all images have finished loading.
 */
const PreloadImages = ({ srcs, onReady }: Props) => {
  const loadedCount = useRef(0)

  useEffect(() => {
    loadedCount.current = 0
  }, [srcs])

  const handleSettle = () => {
    loadedCount.current += 1
    if (loadedCount.current === srcs.length) {
      onReady?.()
    }
  }

  return (
    <>
      {srcs.map((src) => (
        <Box
          key={src}
          as="img"
          src={src}
          alt=""
          aria-hidden="true"
          position="absolute"
          w="1px"
          h="1px"
          opacity={0}
          pointerEvents="none"
          onLoad={handleSettle}
          onError={handleSettle}
        />
      ))}
    </>
  )
}

export default PreloadImages
